/**
 * Google Reviews Adapter (Story 23.1)
 *
 * SECURITY: read-only. This adapter MUST NOT issue PUT/DELETE to `/reply`
 * endpoints (Google enforces a 10 edits/min/location hard cap; reserved for
 * a future write-mutation story). All review write mutations are out of scope.
 *
 * OAuth FAMILY REUSE: shares the Google OAuth family with GA4, Search Console,
 * Google Ads, YouTube, and Google Business Profile. Declared via
 * `oauthFamily: "google"` on the registry entry — NO new family entry, NO new
 * env keys, NO new queue handlers, NO new OAuth callback route. See
 * oauth-integration-directive.md Anti-Patterns #1-3.
 *
 * SCOPE: surfaces review-management metrics beyond the aggregate review counts
 * already shipped by Google Business Profile. The GBP adapter remains
 * authoritative for `totalReviewCount`, `averageRating`, `replyRate`, the
 * 1-5 star distribution, and `review_date`/`star_rating`/`reviewer_name`/
 * `has_reply` dimensions. This adapter adds:
 *   - `_reportType: "reviewReplyState"` — APPROVED/PENDING/REJECTED/unknown bucket counts
 *   - `_reportType: "reviewSentiment"` — positive/neutral/negative bucket counts (rule-based)
 *   - `_reportType: "reviewReplySummary"` — reply latency, rating-only ratio,
 *     pending/rejected count, with-text count, anonymous reviewer count
 *   - `_reportType: "googleReview"` — per-review record (distinct from GBP's "review")
 *     with `reviewerIsAnonymous`, `replyState`, and the new `reviewReplyState` field
 *
 * DATE-RANGE CAVEAT: Google's v4 reviews list endpoint does not support a date
 * filter — page 1 returns the most recent 50 reviews globally. `replyRate` and
 * `avgReplyLatencySeconds` therefore measure the last 50 reviews, not a
 * date-range slice. Widget renderers (Story 23.2) surface a "data based on
 * most recent 50 reviews" footnote where these metrics appear.
 *
 * RELATIONSHIP TO GBP: shares OAuth tokens and shares discovery via
 * `discoverBusinessProfiles`. Both adapters operate on the SAME Google
 * `accountId` (location ID) — Summit Legal `demo-300002` is the canonical
 * shared example.
 *
 * CALLER CONTRACT (Round 1 Code Review): the `ctx.accountId` passed in MUST
 * have been validated upstream to belong to `ctx.tenantId` (per the
 * cross-tenant invariant in `_bmad/bmm/data/oauth-integration-directive.md`).
 * The adapter validates shape (numeric, length) but does NOT re-validate
 * ownership against the DB — that's the route handler's responsibility (see
 * the GBP route at `apps/server/src/routes/integrations/google-business-profile.ts`
 * for the canonical pattern). This adapter additionally provides
 * defense-in-depth via the `gbp_meta:{integrationId}` KV side-channel which
 * is derived from `integrationId` only (never user-supplied).
 */

import type {
  IntegrationAdapter,
  AdapterContext,
  OAuthCredentials,
  AdapterResult,
  AdapterMetrics,
  AccountInfo,
  MetricDefinition,
  DimensionDefinition,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { API_VERSIONS } from "../config";
import { discoverBusinessProfiles } from "./discovery";
import {
  STAR_RATING_MAP,
  sanitizeReviewText,
  redactGbpPaths,
  classify403Error,
  buildHeaders,
  type ReviewsResponse,
} from "./gbp";

// ============================================================================
// Constants
// ============================================================================

const PLATFORM = "google-reviews" as const;

const REVIEWS_BASE = `https://mybusiness.googleapis.com/${API_VERSIONS.mybusinessLegacy}`;

/** Per-call timeout for the v4 reviews list call. */
const QUERY_TIMEOUT_MS = 5_000;

/** Validation timeout — same posture as gbpAdapter. */
const VALIDATE_TIMEOUT_MS = 5_000;

/** Max anonymous-reviewer display name length passed through sanitizeReviewText. */
const REVIEWER_NAME_MAX_LENGTH = 100;

/** Max review-comment length passed through sanitizeReviewText. */
const REVIEW_COMMENT_MAX_LENGTH = 300;

/** Max reply-comment length passed through sanitizeReviewText. */
const REPLY_COMMENT_MAX_LENGTH = 300;

/** Anonymous reviewer display sentinel — replaces Google's possibly-cached anonymized name. */
const ANONYMOUS_REVIEWER_LABEL = "Anonymous";

/**
 * Story 23.1 Round 3 Red Team #5 + Round 1+2 Code Review — CSV-injection
 * prefix strip. Round 2 hardening:
 *   - Added zero-width chars (U+200B-U+200D, U+FEFF) which JS `\s` does
 *     NOT match. An attacker prefix like `"​=cmd"` previously
 *     bypassed the guard; now stripped.
 *   - This regex is run AFTER `sanitizeReviewText` (not before) so that
 *     leading HTML tags like `"<>=cmd"` — which sanitizeReviewText
 *     strips, exposing the `=` — can't slip through. Pipeline order:
 *     sanitizeReviewText → stripCsvInjectionPrefix → final value.
 *   - Applied to ALL free-text fields (reviewer name + comment + reply
 *     comment), not just the name.
 */
// Includes formula-injection chars (`=+-@`), whitespace (`\s\t\r`), and
// zero-width / invisible Unicode that JS `\s` does NOT match:
//   - U+200B (ZWSP), U+200C (ZWNJ), U+200D (ZWJ), U+FEFF (BOM)
//   - U+180E (MONGOLIAN VOWEL SEPARATOR)
//   - U+2060 (WORD JOINER), U+2061-U+2064 (invisible math ops)
//   - U+206A-U+206F (deprecated inhibit-shaping controls)
// Round 3 extension catches U+2060/U+180E bypass found by Edge Case Hunter.
const CSV_INJECTION_LEADING_CHARS = new RegExp(
  "^[\\s\\t\\r=+\\-@\\u180E\\u200B\\u200C\\u200D\\u2060\\u2061\\u2062\\u2063\\u2064\\u206A\\u206B\\u206C\\u206D\\u206E\\u206F\\uFEFF]+",
  "u",
);

/**
 * Round 1+2 Code Review — strict ISO-8601-with-timezone shape check.
 * `Date.parse` accepts non-spec strings like `"2026-01-10T10:00:00"`
 * (no Z, no offset — implementation-defined per ECMA-262), yielding
 * silently-wrong latency math on non-UTC Workers. Round 2 tightening:
 *   - Year bounded to 1900-2099 (was unbounded `\d{4}` which accepted
 *     year 0000).
 *   - Fractional seconds capped at 1-9 digits (was unbounded `\d+`).
 * Note: this is a SHAPE check; `Date.parse` is the actual validator —
 * the regex prevents the silently-wrong TZ-less local-time interpretation.
 */
const STRICT_ISO_8601_TZ =
  /^(?:19|20)\d{2}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/;

/**
 * Round 1 Code Review — maximum sane reply latency (60 days in seconds).
 * Google states replies usually clear in <10 min but can take up to 30 days
 * for moderation. Anything beyond 60 days is treated as clock skew / bad
 * timestamp and excluded from the latency average.
 */
const MAX_SANE_REPLY_LATENCY_SECONDS = 60 * 24 * 60 * 60;

/**
 * Round 1+3 Code Review — Google's documented `starRating` enum values,
 * DERIVED from `STAR_RATING_MAP` (gbp.ts) so a future addition (e.g. a
 * 6th rating) cascades automatically without per-file edits — preserves
 * the AC #7 single-source-of-truth invariant. Any unrecognized value is
 * mapped to 0 AND emits a structured log line (same graceful-degradation
 * pattern as `normalizeReplyState`).
 */
const KNOWN_STAR_RATING_VALUES = new Set(Object.keys(STAR_RATING_MAP));

// ============================================================================
// Extended Reviews Response (adds reviewReplyState + isAnonymous)
// ============================================================================

/**
 * Local extension of the GBP `ReviewsResponse` shape to include fields the
 * 2026-04-01 v4 update added: `reviewReplyState` and `reviewer.isAnonymous`.
 * The base `ReviewsResponse` type is reused from `gbp.ts` for everything else.
 *
 * Story 35.6 (2026-05-21) — extended `reviews[]` items with optional
 * `reviewMediaItems` array per Google's 2026-04-20 v4 additive (verified
 * developers.google.com/my-business/reference/rest/v4/accounts.locations.reviews
 * 2026-05-19). The field carries reviewer-attached photos and videos. The
 * array is typed `readonly` to match the existing `reviews` array style
 * (no mutation in the aggregation pass).
 */
type ReviewReplyState = "APPROVED" | "PENDING" | "REJECTED";

/**
 * Story 35.6 — single source of truth for the 2026-04-20 v4
 * `reviewMediaItems[]` item shape. Exported so the FDL generator + tests
 * stay structurally aligned with the production schema; future Google
 * additives only need one type edit. All fields are optional + readonly
 * per the v4 contract (verified
 * `developers.google.com/my-business/reference/rest/v4/accounts.locations.reviews`
 * 2026-05-19).
 */
export interface ReviewMediaItem {
  readonly thumbnailUrl?: string;
  readonly thumbnailLabel?: string;
  readonly videoUrl?: string;
}

interface GoogleReviewsResponse extends ReviewsResponse {
  reviews?: Array<{
    reviewId?: string;
    reviewer?: {
      displayName?: string;
      profilePhotoUrl?: string;
      isAnonymous?: boolean;
    };
    starRating?: string;
    comment?: string;
    createTime?: string;
    updateTime?: string;
    reviewReply?: {
      comment?: string;
      updateTime?: string;
      /** Added 2026-04-01 by Google — PENDING / REJECTED / APPROVED. */
      reviewReplyState?: string;
    };
    /**
     * Story 35.6 — Added 2026-04-20 by Google. Reviewer-attached photos
     * (`thumbnailUrl` + optional `thumbnailLabel`) and videos (`videoUrl`).
     * Server-side: NOT returned for anonymous reviewers (Google's
     * anonymization policy, verified 2026-05-19).
     */
    reviewMediaItems?: ReadonlyArray<ReviewMediaItem>;
  }>;
}

// ============================================================================
// Helpers
// ============================================================================

/** Round 1+2+3 Code Review — distinguish the empty-state reasons for operators. */
type EmptyStateReason =
  | "kv_missing"
  | "kv_malformed"
  | "kv_read_failed"
  | "discovery_not_found"
  | "discovery_ambiguous"
  | "google_returned_zero";

/**
 * Round 1+3 Code Review — distinguish KV-miss, KV-read-failed,
 * KV-parse-failure, and KV-missing-field. Returns either a parsed
 * `parentAccountId` or the specific reason for failure (so callers can
 * decide between rediscovery, empty-state, or error).
 *
 * Round 3 fix: a KV read throw is NO LONGER conflated with "missing".
 * During a Cloudflare KV partial outage, treating reads as misses
 * triggers rediscovery on every fetch and burns the project's shared
 * 300 QPM Google quota across all six Google-family adapters. The
 * distinct `kv_read_failed` reason is logged so operators can alert and
 * skips the rediscovery storm.
 */
async function readParentAccountIdFromKv(
  ctx: AdapterContext,
): Promise<{ value: string } | { reason: EmptyStateReason }> {
  let metaRaw: string | null;
  try {
    const kv = ctx.env.KV as {
      get(key: string): Promise<string | null>;
    };
    metaRaw = await kv.get(`gbp_meta:${ctx.integrationId}`);
  } catch (err) {
    console.log(
      JSON.stringify({
        event: "google_reviews_kv_read_failed",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        // Truncate to 200 chars — defends against future runtime
        // emitting unbounded provider-controlled strings.
        error: (err instanceof Error ? err.message : String(err)).slice(0, 200),
        timestamp: new Date().toISOString(),
      }),
    );
    return { reason: "kv_read_failed" };
  }
  if (metaRaw === null) return { reason: "kv_missing" };

  let meta: { parentAccountId?: unknown };
  try {
    meta = JSON.parse(metaRaw) as { parentAccountId?: unknown };
  } catch {
    return { reason: "kv_malformed" };
  }
  if (typeof meta.parentAccountId !== "string") {
    return { reason: "kv_malformed" };
  }
  return { value: meta.parentAccountId };
}

/**
 * Round 1 Code Review — Task 3.5 backfill. When `gbp_meta:{integrationId}`
 * is missing, run a fresh discovery via `discoverBusinessProfiles`, locate
 * the AccountInfo whose `id === ctx.accountId`, and persist its
 * `metadata.parentAccountId` back to KV under the google-reviews
 * integration's own integrationId. Returns the discovered parentAccountId
 * on success, or `undefined` if discovery yields no match.
 *
 * Cross-tenant invariant: the KV key is derived from `ctx.integrationId`
 * only — never user input. The discovery itself is OAuth-scoped to the
 * caller's credentials.
 */
async function rediscoverAndPersistParentAccountId(
  ctx: AdapterContext,
  credentials: OAuthCredentials,
): Promise<{ value: string } | { reason: "discovery_not_found" | "discovery_ambiguous" }> {
  const discoveryResult = await discoverBusinessProfiles(credentials.accessToken);
  if (!discoveryResult.success) return { reason: "discovery_not_found" };

  // Round 2 Code Review — guard against multiple discovery entries with
  // the same `id`. A location migrated between brands or a multi-brand
  // chain can legitimately surface twice under different parents.
  // Picking the first match silently could route fetches to the wrong
  // parent and (in pathological misconfigurations) cross-tenant. Distinct
  // `discovery_ambiguous` reason so operators triage rather than serving
  // potentially wrong data.
  const matches = discoveryResult.data.filter((acc) => acc.id === ctx.accountId);
  if (matches.length === 0) return { reason: "discovery_not_found" };
  if (matches.length > 1) {
    const parentIds = matches
      .map((m) => m.metadata?.parentAccountId)
      .filter((p): p is string => typeof p === "string");
    // Round 3 Code Review — fall back to `matches.length` + first name
    // when none of the candidates carry a typed parentAccountId, so
    // operators still get triage signal ("N matches, e.g. 'Brand A'").
    console.log(
      JSON.stringify({
        event: "google_reviews_discovery_ambiguous",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        accountId: ctx.accountId,
        matchCount: matches.length,
        firstMatchName: matches[0]?.name,
        candidateParentIds: parentIds,
        timestamp: new Date().toISOString(),
      }),
    );
    return { reason: "discovery_ambiguous" };
  }

  const match = matches[0]!;
  const parentAccountId = match.metadata?.parentAccountId;
  if (typeof parentAccountId !== "string" || !/^\d+$/.test(parentAccountId)) {
    return { reason: "discovery_not_found" };
  }

  // Best-effort persistence — a KV write failure is non-blocking. The
  // adapter will rediscover on the next call. Round 2 Code Review: emit
  // a forensic log so operators can alert on a partial KV outage that
  // would otherwise burn the project's 300 QPM budget via repeated
  // re-discovery.
  try {
    const kv = ctx.env.KV as {
      put(key: string, value: string): Promise<void>;
    };
    await kv.put(
      `gbp_meta:${ctx.integrationId}`,
      JSON.stringify({ parentAccountId }),
    );
  } catch (err) {
    console.log(
      JSON.stringify({
        event: "google_reviews_kv_write_failed",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        // Round 3 Code Review — truncate to 200 chars to defend against
        // future runtime emitting unbounded provider-controlled strings
        // (e.g. KV runtime echoing key/value snippets in diagnostics).
        error: (err instanceof Error ? err.message : String(err)).slice(0, 200),
        timestamp: new Date().toISOString(),
      }),
    );
  }

  return { value: parentAccountId };
}

/**
 * Round 1+2 Code Review — Google reviewer `profilePhotoUrl` URL allowlist.
 * Drops the URL (returns `undefined`) unless it satisfies ALL of:
 *   - parses as a valid URL via `new URL(...)`
 *   - protocol is exactly `https:`
 *   - hostname matches `lh{N}.googleusercontent.com`
 *   - has NO userinfo (`username` / `password` are empty)
 *
 * Round 2 hardening: previously a regex-only check let userinfo URLs like
 * `https://attacker:tracking-token@lh3.googleusercontent.com/x` through
 * — the regex matched the scheme + host but didn't see the embedded
 * credentials. `new URL(...)` parses the full RFC 3986 authority
 * component, so userinfo is detected and rejected.
 *
 * Anonymous reviewers ALWAYS have their photo URL dropped regardless of
 * host — anonymous reviewers should have no identifying surface at all.
 */
function safeProfilePhotoUrl(
  rawUrl: string | undefined,
  isAnonymous: boolean,
): string | undefined {
  if (isAnonymous) return undefined;
  if (!rawUrl) return undefined;
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return undefined;
  }
  if (parsed.protocol !== "https:") return undefined;
  if (!/^lh\d+\.googleusercontent\.com$/.test(parsed.hostname)) return undefined;
  if (parsed.username !== "" || parsed.password !== "") return undefined;
  // Round 3 Code Review (High) — return `parsed.href` (the WHATWG-canonical
  // form), NOT `rawUrl`. The URL parser strips leading/trailing whitespace
  // and embedded `\t\r\n` before validating; returning rawUrl would persist
  // the unvalidated input into records, logs, and CSV exports — opening
  // header-injection-style attacks against non-URL-aware downstream
  // consumers. parsed.href is the canonical safe form.
  return parsed.href;
}

/**
 * Round 1 Code Review — `STAR_RATING_UNSPECIFIED` and future-additive
 * Google star-rating enum values silently mapped to 0 → silently
 * dropped from sentiment buckets. Mirror the `normalizeReplyState`
 * pattern: log unknown values once for forensic capture, return null
 * so the caller can decide.
 */
function normalizeStarRating(
  raw: string | undefined,
  knownUnknownStarRatings: Set<string>,
): number {
  if (raw === undefined || raw === "") return 0;
  if (KNOWN_STAR_RATING_VALUES.has(raw)) {
    return STAR_RATING_MAP[raw] ?? 0;
  }
  // Round 1 Code Review — dedupe forensic logs across the fetch via a
  // request-scoped Set so a 50-review page with all-unknown ratings emits
  // 1 log line, not 50.
  if (!knownUnknownStarRatings.has(raw)) {
    knownUnknownStarRatings.add(raw);
    console.log(
      JSON.stringify({
        event: "google_reviews_unknown_star_rating",
        starRating: raw,
        timestamp: new Date().toISOString(),
      }),
    );
  }
  return 0;
}

/**
 * Bucket a 1-5 star rating into positive/neutral/negative.
 * Rule-based per integration-metrics/google-reviews.md — Google does not
 * expose a sentiment field, `starRating` is the only signal.
 */
export function sentimentBucketForStar(star: number): "positive" | "neutral" | "negative" {
  if (star >= 4) return "positive";
  if (star === 3) return "neutral";
  return "negative";
}

/**
 * Compute reply latency in seconds between createTime and reviewReply.updateTime.
 * Returns null when either timestamp is missing, fails strict ISO-8601 parsing,
 * has a negative delta (clock skew), or exceeds the 60-day sane upper bound.
 *
 * Round 1 Code Review:
 *  - Strict ISO check (regex) defends against TZ-less strings that V8 parses
 *    as local time on non-UTC Workers (silently wrong latency math).
 *  - Negative-delta and out-of-range deltas are dropped; the upstream caller
 *    must NOT also increment `approvedReplyCount` for these samples to keep
 *    the numerator/denominator consistent across `replyRate` and
 *    `avgReplyLatencySeconds` (Round 1 finding #9).
 */
function computeReplyLatencySeconds(
  createTime: string | undefined,
  replyUpdateTime: string | undefined,
): number | null {
  if (!createTime || !replyUpdateTime) return null;
  if (!STRICT_ISO_8601_TZ.test(createTime)) return null;
  if (!STRICT_ISO_8601_TZ.test(replyUpdateTime)) return null;
  const createMs = Date.parse(createTime);
  const replyMs = Date.parse(replyUpdateTime);
  if (!Number.isFinite(createMs) || !Number.isFinite(replyMs)) return null;
  const deltaMs = replyMs - createMs;
  if (deltaMs < 0) return null;
  const seconds = deltaMs / 1000;
  // Round 2 Code Review — `>=` so exactly 60 days is excluded; Google
  // states max moderation window is 30 days so 60 days is a 2× buffer.
  if (seconds >= MAX_SANE_REPLY_LATENCY_SECONDS) return null;
  return seconds;
}

/**
 * Normalize and validate a `reviewReplyState`. Unknown values pass through as
 * the literal `"unknown"` AND emit a forensic structured log line so a future
 * additive Google state change is captured the day it surfaces.
 *
 * Round 1 Code Review — log-dedup: when a `knownUnknownStates` Set is
 * supplied, repeats of the same unknown value within a single fetch emit
 * only one log line (defends against a 50-review page with all-unknown
 * states emitting 50 redundant logs).
 */
function normalizeReplyState(
  raw: string | undefined,
  knownUnknownStates?: Set<string>,
): "APPROVED" | "PENDING" | "REJECTED" | "unknown" | null {
  if (raw === undefined) return null;
  // Round 3 Code Review — empty string is a common JSON-API "field absent"
  // representation (Google's protobuf JSON sometimes serializes optional
  // enums this way). Treat it as null (legacy/absent), NOT as a phantom
  // unknown state that would spam the forensic log.
  if (raw === "") return null;
  if (raw === "APPROVED" || raw === "PENDING" || raw === "REJECTED") {
    return raw;
  }
  // Forensic log so adapters catch a future 4th state the day it ships.
  // No PII included. Dedup across the request when caller passes a Set.
  if (!knownUnknownStates || !knownUnknownStates.has(raw)) {
    knownUnknownStates?.add(raw);
    console.log(
      JSON.stringify({
        event: "google_reviews_unknown_reply_state",
        state: raw,
        timestamp: new Date().toISOString(),
      }),
    );
  }
  return "unknown";
}

/**
 * Strip the CSV-injection prefix chars (Round 3 Red Team #5) from a reviewer
 * name BEFORE sanitizeReviewText runs its HTML/control/bidi pass.
 */
function stripCsvInjectionPrefix(name: string): string {
  return name.replace(CSV_INJECTION_LEADING_CHARS, "");
}

// ============================================================================
// Story 23.2 — Fixture-mode helper (loadGoogleReviewsFixture)
// ============================================================================

/**
 * Story 23.2 Task 5 — fixture-mode loader. Invoked from the very first
 * statement of `fetchMetrics` when `ctx.fixtures === true`. Mirrors the
 * CallRail 22.2 + CTM 22.4 pattern with FOUR divergences:
 *
 *   1. Summit Legal `platforms[]` IS NOT modified — `"google-reviews"` is
 *      already pre-wired at index 8 by Story 23.1. (DIVERGENT from CTM
 *      22.4's append-only extension; SAME as CallRail 22.2's posture.)
 *   2. NO retention pre-flight — Google Reviews has no retention ceiling
 *      (SAME as CTM 22.4; DIVERGENT from CallRail's 25-month bypass).
 *   3. NO date-range filter — `filterFixtureByDateRange` is SKIPPED
 *      because Google's v4 reviews list endpoint has no date filter
 *      (production parity requires returning "last 50 reviews globally"
 *      regardless of dateRange). Round 2 Pre-mortem D.
 *   4. NO `gbp_meta:*` KV side-channel read — the fixture path NEVER
 *      reaches `readParentAccountIdFromKv`. Round 3 Red Team #1 cross-
 *      tenant isolation invariant.
 *
 * Structured-log telemetry (AC #18) — exactly ONE of these 4 events fires:
 *   - `google_reviews_demo_fixture_loaded` (success)
 *   - `google_reviews_demo_fixture_missing` (KV miss)
 *   - `google_reviews_demo_fixture_binding_missing` (KV or DB missing)
 *   - `google_reviews_demo_fixture_resolver_failed` (getClientSlug throw)
 */
async function loadGoogleReviewsFixture(
  ctx: AdapterContext,
  dateRange: { start: Date; end: Date },
): Promise<AdapterResult<AdapterMetrics>> {
  const env = ctx.env as unknown as Record<string, unknown>;
  const kv = env.KV as unknown;
  const db = env.DB as unknown;

  // AC #18 — binding-missing telemetry events.
  if (!kv || typeof (kv as { get?: unknown }).get !== "function") {
    console.log(
      JSON.stringify({
        event: "google_reviews_demo_fixture_binding_missing",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        missingBinding: "KV",
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "permission_denied",
      message: "Missing KV binding — cannot load Google Reviews demo fixture",
      retryable: false,
    });
  }
  if (!db) {
    console.log(
      JSON.stringify({
        event: "google_reviews_demo_fixture_binding_missing",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        missingBinding: "DB",
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "permission_denied",
      message:
        "Missing DB binding — cannot resolve Google Reviews demo client slug",
      retryable: false,
    });
  }

  // Defensive validation that ctx.tenantId + ctx.clientId are non-empty
  // strings BEFORE calling getClientSlug. Round 3 Red Team Attack #1
  // cross-tenant leak protection (same posture as CTM 22.4 + CallRail 22.2).
  if (
    typeof ctx.tenantId !== "string" ||
    ctx.tenantId.length === 0 ||
    typeof ctx.clientId !== "string" ||
    ctx.clientId.length === 0
  ) {
    return adapterErr({
      code: "permission_denied",
      message:
        "Google Reviews demo fixture: ctx.tenantId and ctx.clientId must be non-empty strings",
      retryable: false,
    });
  }

  // Lazy-imports — skip filterFixtureByDateRange per Round 2 Pre-mortem D
  // (Google v4 has no date filter — production parity requires returning
  // the full 45-review window regardless of dateRange).
  const { getFixtureFromKV } = await import("../fixtures/kv-loader");
  const { getClientSlug } = await import("../fixtures/demo-fetch");

  let clientSlug: string;
  try {
    // Round 3 Red Team Attack #1 — getClientSlug enforces the
    // (tenantId, clientId) scope at the SQL query layer.
    clientSlug = await getClientSlug(
      db as Parameters<typeof getClientSlug>[0],
      ctx.tenantId,
      ctx.clientId,
    );
  } catch (err) {
    console.log(
      JSON.stringify({
        event: "google_reviews_demo_fixture_resolver_failed",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        clientId: ctx.clientId,
        errorClass: err instanceof Error ? err.constructor.name : typeof err,
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "account_not_found",
      message: `Google Reviews demo client not found: ${err instanceof Error ? err.message : String(err)}`,
      retryable: false,
    });
  }

  const startedAt = Date.now();
  const metrics = await getFixtureFromKV(
    kv as Parameters<typeof getFixtureFromKV>[0],
    "google-reviews",
    clientSlug,
  );

  if (metrics === null) {
    const kvKey = `fixture:google-reviews:${clientSlug}`;
    console.log(
      JSON.stringify({
        event: "google_reviews_demo_fixture_missing",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        clientSlug,
        kvKey,
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "account_not_found",
      message: `Google Reviews demo fixture not yet uploaded for client ${clientSlug}; run pnpm fixtures:upload`,
      retryable: false,
    });
  }

  // Pass 1 Blind Hunter C2 — shape validation. `getFixtureFromKV` returns
  // null on malformed JSON but a structurally-valid-but-semantically-wrong
  // payload (wrong platform, missing records array, schema mismatch) would
  // otherwise be served to the renderer with adapterOk framing. Validate
  // the load-bearing invariants before stamping metadata.
  //
  // Pass 2 Edge Hunter H1 — extended validation to assert every record is
  // a non-null object.
  //
  // Pass 3 Blind Hunter M1 — extended to also assert `metrics` itself is
  // a non-null object. If `getFixtureFromKV` ever returns a non-null
  // primitive (e.g., `JSON.parse("42")` → 42), property access on
  // `metrics.platform` would throw `TypeError` BEFORE the validation log
  // fires.
  const isValidShape =
    typeof metrics === "object" &&
    metrics !== null &&
    metrics.platform === "google-reviews" &&
    Array.isArray(metrics.records) &&
    metrics.records.every((r) => r !== null && typeof r === "object");
  if (!isValidShape) {
    console.log(
      JSON.stringify({
        event: "google_reviews_demo_fixture_corrupt",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        clientSlug,
        kvKey: `fixture:google-reviews:${clientSlug}`,
        cachedPlatform: metrics.platform,
        recordsType: Array.isArray(metrics.records) ? "array" : typeof metrics.records,
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "invalid_response",
      message: `Google Reviews demo fixture for client ${clientSlug} is corrupt (wrong platform or non-array records); re-run pnpm fixtures:generate + pnpm fixtures:upload`,
      retryable: false,
    });
  }

  // Round 2 Pre-mortem D — DO NOT call filterFixtureByDateRange. Google's
  // v4 reviews list endpoint has no date filter; fixture-mode parity
  // requires returning the full review set regardless of dateRange.
  //
  // Pass 1 Blind Hunter C1 — derive totalReviewCount + fetchedReviewCount
  // from the actual records rather than hardcoding `45`. Hardcoding
  // would silently lie for any future demo client with a different
  // review count. dataFreshnessLagHours + dataIsPartial remain
  // hard-stamped as fixture-mode invariants (lag is always 0; fixtures
  // are never partial — we shipped exactly what was generated).
  const cachedMeta = metrics.metadata ?? {};
  const googleReviewCount = metrics.records.filter(
    (r) => (r as { _reportType?: string })._reportType === "googleReview",
  ).length;
  // Pass 1 Edge Hunter M1 + Pass 3 Auditor F4 — single clock read used
  // for both the result.fetchedAt stamp AND the structured log's
  // durationMs+timestamp. AC #7 Step 12 requires the helper to return a
  // FRESH fetchedAt (not a stale value spread from the KV payload).
  const completedAt = new Date();
  const result: AdapterMetrics = {
    ...metrics,
    // Pass 1 Auditor M-1 — AC #7 Step 12 accountId fallback. Prefer the
    // request's accountId; fall back to the fixture's stamped accountId.
    // Story 23.7 Round 1 Edge Hunter Finding #5 removed the static
    // `"demo-300002"` (Summit Legal's GBP location) fallback because
    // GreenLife now ships google-reviews with its own `demo-2300007`
    // location — falling back to a hard-coded Summit Legal ID would
    // cross-leak the identifier into a GreenLife payload.
    // Story 23.7 Round 3 Code Review Edge Hunter Finding R3-EH-09 fix —
    // Round 1's empty-string fallback was a silent collision risk
    // (cache keys keyed on `""` would alias across tenants). Now throw
    // a fail-loud error when BOTH `ctx.accountId` and `metrics.accountId`
    // are unset, surfacing the misconfiguration immediately rather than
    // producing malformed cache keys / HTTP URLs downstream.
    accountId: (() => {
      const resolved = ctx.accountId || metrics.accountId;
      if (!resolved) {
        throw new Error(
          "google_reviews_fixture_missing_accountId: Google Reviews fixture-mode helper requires `ctx.accountId` or `metrics.accountId` — both are empty. This usually indicates a misconfigured demo workspace or a fixture JSON missing the `accountId` field.",
        );
      }
      return resolved;
    })(),
    dateRange,
    metadata: {
      ...cachedMeta,
      dataFreshnessLagHours: 0,
      dataIsPartial: false,
      totalReviewCount: googleReviewCount,
      fetchedReviewCount: googleReviewCount,
    },
    // Pass 1 Blind Hunter C3 — derive recordCount from actual records;
    // never trust the cached payload's stale value.
    recordCount: metrics.records.length,
    // Pass 3 Auditor F4 — refresh fetchedAt per AC #7 Step 12. Without
    // this, the cached payload's fetchedAt (potentially weeks old) leaks
    // into the response and downstream "data freshness" timestamps lie.
    fetchedAt: completedAt,
  };
  console.log(
    JSON.stringify({
      event: "google_reviews_demo_fixture_loaded",
      integrationId: ctx.integrationId,
      tenantId: ctx.tenantId,
      clientSlug,
      recordCount: result.recordCount,
      durationMs: completedAt.getTime() - startedAt,
      timestamp: completedAt.toISOString(),
    }),
  );

  return adapterOk(result);
}

// ============================================================================
// v4 Reviews Fetch
// ============================================================================

/**
 * Story 35.6 — Google's documented partial-response query
 * (developers.google.com/discovery/v1/performance#partial-response,
 * verified 2026-05-19). Explicitly requests `reviewMediaItems` (Google's
 * 2026-04-20 v4 additive) plus all other fields the aggregator reads. Top-
 * level fields (`totalReviewCount`, `averageRating`, `nextPageToken`) MUST
 * stay listed alongside `reviews(...)` — Google's partial-response semantics
 * omit anything not requested, and the aggregator depends on
 * `totalReviewCount`.
 */
const REVIEWS_FIELDS_PARAM =
  "reviews(reviewId,reviewer,starRating,comment,createTime,updateTime,reviewReply,reviewMediaItems),totalReviewCount,averageRating,nextPageToken";

/**
 * Fetch reviews from the legacy v4 API using both `parentAccountId` AND
 * `locationId`. Page 1 only (pageSize=50) per Story 23.1 Worker-timeout budget.
 *
 * Captures the 2026-04-01 `reviewReplyState` field AND
 * `reviewer.isAnonymous` — additive vs the existing gbp.ts `fetchReviews`.
 *
 * Story 35.6 — Adds the `&fields=...` partial-response parameter to
 * explicitly request `reviewMediaItems` (Google's 2026-04-20 v4 additive
 * field). Wraps the fetch in a try-once-with-fields, retry-once-without-
 * fields pattern: if Google returns HTTP 400 AND the response body mentions
 * "fields" (case-insensitive), the adapter retries without the parameter
 * AND emits a structured log so operators see the degradation. The retry
 * is capped at 1 attempt — `&fields=` rejection is a deterministic config
 * error, not a transient failure (no exponential backoff).
 */
async function fetchReviewsV4(
  parentAccountId: string,
  locationId: string,
  headers: Record<string, string>,
): Promise<AdapterResult<GoogleReviewsResponse>> {
  const resourcePath = `accounts/${parentAccountId}/locations/${locationId}`;
  if (!/^accounts\/\d+\/locations\/\d+$/.test(resourcePath)) {
    return adapterErr({
      code: "invalid_response",
      message: "Invalid GBP resource path for reviews",
      retryable: false,
    });
  }

  const baseUrl = `${REVIEWS_BASE}/${resourcePath}/reviews?pageSize=50`;
  // Story 35.6 — `&fields=` value passed unencoded. The characters in
  // Google's partial-response syntax — `(`, `)`, `,`, `/` — are all
  // RFC 3986 query-component-legal sub-delims; Google's own docs show
  // them unencoded. URL-encoding would still be accepted server-side
  // but unencoded is the documented form.
  const urlWithFields = `${baseUrl}&fields=${REVIEWS_FIELDS_PARAM}`;
  let result = await safeFetch(urlWithFields, { headers }, QUERY_TIMEOUT_MS);

  // Story 35.6 — graceful degradation: if Google rejects the `&fields=`
  // parameter (e.g., future deprecation OR field-path syntax change), retry
  // once without the parameter so the fetch still returns valid data using
  // Google's default field set. Detection: HTTP 400 AND response body
  // matches one of Google's documented partial-response rejection
  // phrases — `"invalid field selection"` (returned for an unknown
  // subfield in the `fields` value) OR `"invalid parameter 'fields'"`
  // (returned for malformed top-level syntax). Cap retry at 1 attempt
  // — `&fields=` rejection is deterministic, not transient.
  //
  // Round 1 code review fix — earlier broad-match `.includes("field")`
  // false-positive triggered on unrelated 400s like
  // `"Invalid pageSize field value"` or
  // `"Required field 'parent' is missing"`. Those caused wasted retries
  // + misleading `google_reviews_fields_param_unsupported` log lines
  // that pointed operators at the wrong cause.
  // Round 2 code review: phrase list intentionally specific to Google's
  // documented partial-response rejection wording — broader matchers
  // (Round 1 had `.includes("field")`) false-positive on unrelated 400s
  // like `"Invalid pageSize field value"`. Each phrase is lower-cased
  // because we compare against `providerMessage.toLowerCase()`. Variants
  // cover both ASCII straight-quote styles around `fields` and the
  // bare-selection form. Curly/smart quotes (U+201C/U+201D) are NOT
  // included — Google's API surface uses ASCII, and proxies that
  // normalize to curly would be unusual; if Google ever rewords with
  // additional variants, extend this list rather than re-broadening
  // the matcher.
  const FIELDS_REJECTION_PHRASES = [
    "invalid field selection",
    "invalid parameter 'fields'",
    "invalid parameter \"fields\"",
    "invalid 'fields' parameter",
    "invalid \"fields\" parameter",
    "unknown field in 'fields'",
    "unknown field in \"fields\"",
    // Round 3 code review — additional Google partial-response error
    // wordings observed in Discovery API documentation and protobuf-
    // backed v4 endpoints. `field mask` is the Discovery API's term
    // for the same concept (partial-response selector); `cannot parse`
    // is a documented variant for malformed selector syntax.
    "invalid field mask",
    "cannot parse 'fields'",
    "cannot parse \"fields\"",
  ];
  const providerMessage = !result.success ? result.error.providerMessage : undefined;
  const providerStatus = !result.success ? result.error.providerStatus : undefined;
  const looksLikeFieldsRejection =
    providerStatus === 400 &&
    typeof providerMessage === "string" &&
    FIELDS_REJECTION_PHRASES.some((phrase) =>
      providerMessage.toLowerCase().includes(phrase),
    );
  if (!result.success && looksLikeFieldsRejection) {
    console.log(
      JSON.stringify({
        event: "google_reviews_fields_param_unsupported",
        status: result.error.providerStatus,
        timestamp: new Date().toISOString(),
      }),
    );
    result = await safeFetch(baseUrl, { headers }, QUERY_TIMEOUT_MS);
  }

  if (!result.success) {
    if (result.error.providerStatus === 403) {
      const classified = classify403Error(result.error.providerMessage);
      if (classified) {
        return adapterErr({
          ...result.error,
          code: classified.code,
          message: redactGbpPaths(classified.message),
        });
      }
    }
    return adapterErr({
      ...result.error,
      message: redactGbpPaths(result.error.message),
    });
  }

  try {
    const data = (await result.data.json()) as GoogleReviewsResponse;
    return adapterOk(data);
  } catch (err) {
    return adapterErr({
      code: "invalid_response",
      message: redactGbpPaths(
        sanitizeErrorMessage(
          `Failed to parse Google Reviews response: ${err instanceof Error ? err.message : String(err)}`,
        ),
      ),
      retryable: false,
    });
  }
}

// ============================================================================
// Metric Definitions (AC #12)
// ============================================================================

/**
 * 10 net-new metric definitions. The 10 metrics owned by GBP
 * (`totalReviewCount`, `averageRating`, `reviewsInPeriod`, `avgRatingInPeriod`,
 * `replyRate`, five-star → one-star counts) are intentionally NOT redeclared
 * here — GBP stays authoritative for those.
 */
export const GOOGLE_REVIEWS_METRICS: MetricDefinition[] = [
  // ── Review Volume (3) ──
  {
    key: "ratingOnlyCount",
    label: "Rating-Only Reviews",
    type: "number",
    category: "reviews",
    dataType: "Integer",
    aggregationKind: "additive",
  },
  {
    key: "withTextCount",
    label: "Reviews with Text",
    type: "number",
    category: "reviews",
    dataType: "Integer",
    aggregationKind: "additive",
  },
  {
    key: "ratingOnlyPercentage",
    label: "Rating-Only Percentage",
    type: "percentage",
    category: "reviews",
    dataType: "Percentage",
    aggregationKind: "ratio",
  },
  // ── Reply (4) ──
  {
    key: "repliedReviewCount",
    label: "Replied Reviews",
    type: "number",
    category: "reviews",
    dataType: "Integer",
    aggregationKind: "additive",
    description: "Count of reviews with a publicly visible (APPROVED) owner reply.",
  },
  {
    key: "unrepliedReviewCount",
    label: "Unreplied Reviews",
    type: "number",
    category: "reviews",
    dataType: "Integer",
    aggregationKind: "additive",
    description: "Count of reviews with NO owner reply at all. Does NOT include PENDING/REJECTED replies — those are counted separately in pendingOrRejectedReplies.",
  },
  {
    key: "avgReplyLatencySeconds",
    label: "Avg Reply Latency",
    type: "duration",
    category: "reviews",
    dataType: "Duration",
    aggregationKind: "ratio", ratioComponents: { denominator: "repliedReviewCount" },
    description: "Mean seconds between review createTime and APPROVED reply updateTime.",
  },
  {
    key: "pendingOrRejectedReplies",
    label: "Pending/Rejected Replies",
    type: "number",
    category: "reviews",
    dataType: "Integer",
    aggregationKind: "additive",
    description: "Replies that have not yet been APPROVED (PENDING/REJECTED/unknown).",
  },
  // ── Sentiment — rule-based 1-2/3/4-5 (3) ──
  {
    key: "positiveReviewCount",
    label: "Positive Reviews",
    type: "number",
    category: "reviews",
    dataType: "Integer",
    aggregationKind: "additive",
    description: "4-5 star reviews. Rule-based (Google does not provide sentiment).",
  },
  {
    key: "neutralReviewCount",
    label: "Neutral Reviews",
    type: "number",
    category: "reviews",
    dataType: "Integer",
    aggregationKind: "additive",
    description: "3-star reviews.",
  },
  {
    key: "negativeReviewCount",
    label: "Negative Reviews",
    type: "number",
    category: "reviews",
    dataType: "Integer",
    aggregationKind: "additive",
    description: "1-2 star reviews. Rule-based (Google does not provide sentiment).",
  },
  // ── Story 35.6 — Batch 1: Review Media (4) ──
  // Google 2026-04-20 v4 additive (reviewMediaItems). Directly computed
  // from the response; no synthetic flag.
  {
    key: "reviewsWithPhotosCount",
    label: "Reviews with Photos",
    type: "number",
    category: "reviews",
    dataType: "Integer",
    aggregationKind: "additive",
    description:
      "Count of reviews containing at least one photo (reviewMediaItems[].thumbnailUrl present without videoUrl). Google 2026-04-20 additive.",
  },
  {
    key: "reviewsWithVideosCount",
    label: "Reviews with Videos",
    type: "number",
    category: "reviews",
    dataType: "Integer",
    aggregationKind: "additive",
    description:
      "Count of reviews containing at least one video media item (reviewMediaItems[].videoUrl present). Google 2026-04-20 additive.",
  },
  {
    key: "photoMediaItemCount",
    label: "Photo Media Items",
    type: "number",
    category: "reviews",
    dataType: "Integer",
    aggregationKind: "additive",
    description:
      "Total photo media items across all fetched reviews (sum, not per-review count).",
  },
  {
    key: "mediaAttachmentRate",
    label: "Media Attachment Rate",
    type: "percentage",
    category: "reviews",
    dataType: "Percentage",
    aggregationKind: "ratio",
    description:
      "Percentage of reviews carrying at least one photo or video. (photos+videos / total reviews) * 100. NOTE: Google's anonymization policy strips reviewMediaItems from anonymous reviewers server-side (verified 2026-05-19) and the adapter applies the same skip defense-in-depth — so anonymous reviews appear in the denominator but never contribute to the numerator. Clients with a high proportion of anonymous reviews will see a structurally depressed rate.",
  },
  // ── Story 35.6 — Batch 2: Reply State Extras (2) ──
  // Beyond the APPROVED/PENDING/REJECTED count buckets Story 23.1 already
  // emits — these surface the moderation-pipeline efficiency ratios.
  {
    key: "approvalRate",
    label: "Reply Approval Rate",
    type: "percentage",
    category: "reviews",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "repliedReviewCount" },
    description:
      "Percentage of reply attempts that reached APPROVED state. APPROVED / (APPROVED + PENDING + REJECTED) * 100. Null when no reply attempts.",
  },
  {
    key: "rejectedReplyRate",
    label: "Rejected Reply Rate",
    type: "percentage",
    category: "reviews",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "repliedReviewCount" },
    description:
      "Percentage of reply attempts that ended in REJECTED state. Signals policy-violation issues. REJECTED / (APPROVED + PENDING + REJECTED) * 100.",
  },
  // ── Story 35.6 — Batch 3: Review Attributes (2) ──
  // Reviewer-attribute coverage — distinct non-anonymous reviewer count +
  // text-rich review count. Cardinality signals fake-review patterns at
  // the extreme; text-rich count is Google's documented quality signal.
  {
    key: "localizedReviewCount",
    label: "Localized Review Count",
    type: "number",
    category: "reviews",
    dataType: "Integer",
    aggregationKind: "additive",
    description:
      "Reviews with non-empty comment text AND a non-anonymous reviewer. Author-identifiable text signal — excludes rating-only + anonymous.",
  },
  {
    key: "reviewerDiversityCount",
    label: "Reviewer Diversity Count",
    type: "number",
    category: "reviews",
    dataType: "Integer",
    aggregationKind: "additive",
    description:
      "Distinct non-anonymous reviewer display names across fetched reviews. Cardinality signal — low diversity + high volume can indicate competitor-bombing or fake-review patterns.",
  },
  // ── Story 23.2 — synthetic-aggregate metrics (2) ──
  // Computed at preset-expansion time (NOT directly fetched from Google's
  // v4 API). `apiField: "<derived>"` documents the synthetic source per
  // the Moz 20.8 / CallRail 22.2 / CTM 22.4 precedent. Widget-config UI
  // hides synthetic metrics from per-endpoint selectors.
  {
    key: "reviewVelocityPerMonth",
    label: "Review Velocity (per month)",
    type: "number",
    category: "reviews",
    dataType: "Integer",
    aggregationKind: "additive",
    synthetic: true,
    apiField: "<derived>",
    description:
      "Rolling 30-day review count — pace metric. Surfaces 'this practice gets ~N reviews/month' as a single Scorecard KPI.",
  },
  {
    key: "reputationTrendDirection",
    label: "Reputation Trend Direction",
    type: "number",
    category: "reviews",
    dataType: "Integer",
    aggregationKind: "additive",
    synthetic: true,
    apiField: "<derived>",
    description:
      "1 = improving (avgRatingInPeriod > averageRating), 0 = flat, -1 = declining. The Cross-Endpoint Scorecard's 4th candidate KPI.",
  },
];

// ============================================================================
// Dimension Definitions (AC #13)
// ============================================================================

/**
 * 9 net-new dimension definitions. The 4 dimensions owned by GBP
 * (`review_date`, `star_rating`, `reviewer_name`, `has_reply`) are NOT
 * redeclared here.
 */
export const GOOGLE_REVIEWS_DIMENSIONS: DimensionDefinition[] = [
  { key: "review_update_date", label: "Review Update Date", type: "Time", exampleValues: ["2026-03-15"] },
  { key: "reply_date", label: "Reply Date", type: "Time", exampleValues: ["2026-03-16"] },
  {
    key: "sentiment_bucket",
    label: "Sentiment Bucket",
    type: "Review",
    // Round 1 Code Review — null is a valid emission when STAR_RATING_UNSPECIFIED
    // / additive Google enum bypasses the 1-5 buckets.
    exampleValues: ["positive", "neutral", "negative", "null"],
  },
  {
    key: "reviewer_is_anonymous",
    label: "Reviewer Is Anonymous",
    type: "Review",
    exampleValues: ["true", "false"],
  },
  {
    key: "reviewer_profile_photo_url",
    label: "Reviewer Profile Photo URL",
    type: "Review",
    exampleValues: ["https://lh3.googleusercontent.com/..."],
  },
  {
    key: "reply_state",
    label: "Reply State",
    type: "Review",
    // Round 1 Code Review — null is a valid emission (no reply at all);
    // "unknown" surfaces for forecasted additive Google state changes.
    exampleValues: ["APPROVED", "PENDING", "REJECTED", "unknown", "null"],
  },
  // Round 1 Code Review — AC #13 tags `review_id` + `review_comment` as
  // `Review (Identity)`. The DimensionDefinition.type union doesn't include
  // "Identity"; `Review` is the closest match per AC #13 categorization.
  { key: "review_id", label: "Review Id", type: "Review", exampleValues: ["AbFvOq..."] },
  { key: "review_comment", label: "Review Comment", type: "Review", exampleValues: ["Great service"] },
  // Round 2 Code Review — `location` was `Classification` (inconsistent
  // with `review_id`/`review_comment` siblings which use `Review`). All
  // three are AC #13 "Identity"-tagged; the DimensionDefinition union
  // doesn't include "Identity", so `Review` is the closest fallback and
  // we use it consistently across all three.
  {
    key: "location",
    label: "Location",
    type: "Review",
    exampleValues: ["accounts/123/locations/456"],
  },
  // Story 35.6 — Boolean dimension covering photo/video presence in a
  // single breakdown. Derived from `review.reviewMediaItems` non-empty
  // array on the per-review record. Widget config UI filters by this
  // boolean; the photo vs video split is exposed at the metric level
  // (reviewsWithPhotosCount / reviewsWithVideosCount). The
  // DimensionDefinition schema uses `type: "Review"` (the Boolean nature
  // is conveyed via exampleValues — same convention as
  // `reviewer_is_anonymous`).
  {
    key: "has_media",
    label: "Has Media",
    type: "Review",
    exampleValues: ["true", "false"],
  },
];

// ============================================================================
// Adapter
// ============================================================================

export const googleReviewsAdapter: IntegrationAdapter = {
  platform: PLATFORM,

  rateLimits: {
    // Per-project 300 QPM shared across all GBP APIs (verified
    // developers.google.com/my-business/content/limits May 2026). Matches the
    // gbp adapter exactly — duplicating the same number documents the shared
    // budget between the two adapters on the same Google project.
    requestsPerMinute: 300,
  },

  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date },
  ): Promise<AdapterResult<AdapterMetrics>> {
    // Story 23.2 — fixture-mode short-circuit (AC #7). MUST run BEFORE any
    // production-path read: accountId regex validation, KV side-channel
    // read (gbp_meta:*), discovery backfill, v4 fetch. Round 3 Red Team
    // #1 cross-tenant invariant: this gate ensures the `gbp_meta:*` KV
    // namespace is NEVER touched when `ctx.fixtures === true`.
    //
    // Round 5 Edge Case Hunter — strict `=== true` check intentionally
    // rejects truthy-non-true values. A truthy-but-not-strictly-`true`
    // value (e.g. string `"true"` after a future column-type migration)
    // would otherwise silently bypass the gate. Emit a drift log so the
    // surface is visible at runtime BEFORE production code runs.
    const rawFixtures = (ctx as unknown as { fixtures?: unknown }).fixtures;
    if (
      rawFixtures !== undefined &&
      rawFixtures !== true &&
      rawFixtures !== false
    ) {
      console.log(
        JSON.stringify({
          event: "google_reviews_fixtures_flag_unexpected_type",
          integrationId: ctx.integrationId,
          tenantId: ctx.tenantId,
          actualType: typeof rawFixtures,
          timestamp: new Date().toISOString(),
        }),
      );
    }
    const fixturesEnabled = rawFixtures === true;
    if (fixturesEnabled) {
      return loadGoogleReviewsFixture(ctx, dateRange);
    }

    const accountId = ctx.accountId;
    if (!accountId) {
      return adapterErr({
        code: "account_not_found",
        message:
          "No GBP location selected for Google Reviews. Please select a business location first.",
        retryable: false,
      });
    }

    // Round 3 Red Team #3 + AC #6 — locationId URL-injection guard. The
    // spec mandates `/^\d{5,15}$/` for locationId; this matches Google's
    // documented format for Business Profile location IDs (typically
    // 10-15 digits). Round 2 Code Review restored this from a Round 1
    // over-relaxation: gbp.ts uses a looser regex but that's a separate
    // adapter; this story's AC #6 is the contract that governs here.
    if (!/^\d{5,15}$/.test(accountId)) {
      return adapterErr({
        code: "invalid_response",
        message: "Invalid GBP resource path for reviews",
        retryable: false,
      });
    }

    // Round 1 Code Review — Task 3.5 backfill. Distinguish KV-miss /
    // KV-malformed / missing-field. If KV miss, try fresh discovery and
    // persist back to KV for next call.
    const kvLookup = await readParentAccountIdFromKv(ctx);
    let parentAccountId: string;
    if ("value" in kvLookup) {
      parentAccountId = kvLookup.value;
    } else {
      // Round 3 Code Review — operator-actionable reasons short-circuit
      // WITHOUT triggering rediscovery:
      //   - `kv_malformed`: a bad value would get overwritten by a
      //     discovery write, masking the underlying corruption.
      //   - `kv_read_failed`: a KV outage would cause every fetch to
      //     hit `discoverBusinessProfiles`, burning the project's shared
      //     300 QPM Google quota across all 6 Google-family adapters.
      // Only `kv_missing` (genuine cache miss) triggers Task 3.5 backfill.
      if (kvLookup.reason === "kv_malformed" || kvLookup.reason === "kv_read_failed") {
        return emitEmptyState({
          ctx,
          accountId,
          dateRange,
          reason: kvLookup.reason,
        });
      }
      const discovered = await rediscoverAndPersistParentAccountId(ctx, credentials);
      if ("reason" in discovered) {
        return emitEmptyState({
          ctx,
          accountId,
          dateRange,
          reason: discovered.reason,
        });
      }
      parentAccountId = discovered.value;
    }

    // Round 3 Red Team #3 — parentAccountId regex guard (defense-in-depth;
    // KV write paths already validate but a hand-edited KV value could
    // bypass that).
    if (!/^\d+$/.test(parentAccountId)) {
      return adapterErr({
        code: "invalid_response",
        message: "Invalid GBP resource path for reviews",
        retryable: false,
      });
    }

    const headers = buildHeaders(credentials.accessToken);
    const reviewsResult = await fetchReviewsV4(parentAccountId, accountId, headers);
    if (!reviewsResult.success) {
      return adapterErr(reviewsResult.error);
    }

    return aggregateAndEmit({
      ctx,
      accountId,
      parentAccountId,
      dateRange,
      data: reviewsResult.data,
    });
  },

  async validateConnection(
    _ctx: AdapterContext,
    credentials: OAuthCredentials,
  ): Promise<AdapterResult<boolean>> {
    // Lightweight Account Management API call with pageSize=1 — IDENTICAL
    // posture to gbpAdapter.validateConnection per AC #14 ("Same approach
    // as gbpAdapter.validateConnection"). Round 2 reverted a Round 1
    // over-enforcement that added body-shape checks gbp.ts doesn't do —
    // parity is the spec-correct behaviour. We do NOT call the v4 reviews
    // endpoint here because the customer's reviews access may not have
    // been approved at validate time.
    const url = `https://mybusinessaccountmanagement.googleapis.com/${API_VERSIONS.mybusinessAccountManagement}/accounts?pageSize=1`;
    const headers = buildHeaders(credentials.accessToken);
    const result = await safeFetch(url, { headers }, VALIDATE_TIMEOUT_MS);
    if (!result.success) {
      // AC #16 carry-forward — error messages always pass through
      // redactGbpPaths before returning to the caller (defense-in-depth;
      // the underlying gbp.ts doesn't redact in validateConnection but
      // its docstring is silent on the matter and ours is explicit per
      // AC #16, so we keep the redact call here).
      return adapterErr({
        ...result.error,
        message: redactGbpPaths(result.error.message),
      });
    }
    return adapterOk(true);
  },

  async discoverAccounts(
    _ctx: AdapterContext,
    credentials: OAuthCredentials,
  ): Promise<AdapterResult<AccountInfo[]>> {
    // ADR-C: delegate to the existing helper. Both google-business-profile and
    // google-reviews return the same location list — sharing OAuth tokens
    // means sharing discovery.
    const result = await discoverBusinessProfiles(credentials.accessToken);
    if (!result.success) return adapterErr(result.error);
    // Re-stamp each AccountInfo's `platform` so widget config UIs that key off
    // it surface "Google Reviews" rather than "Google Business Profile".
    // Round 2 Code Review — metadata is shallow-cloned (was shared by
    // reference with the GBP discovery output). Avoids accidental
    // cross-adapter mutation if a future caller mutates metadata.
    const remapped: AccountInfo[] = result.data.map((acc) => ({
      ...acc,
      platform: PLATFORM,
      metadata: acc.metadata ? { ...acc.metadata } : undefined,
    }));
    return adapterOk(remapped);
  },

  // Round 1 Code Review — defensive copies prevent caller mutation of the
  // module-level definition arrays.
  getAvailableMetrics(): MetricDefinition[] {
    return [...GOOGLE_REVIEWS_METRICS];
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return [...GOOGLE_REVIEWS_DIMENSIONS];
  },
};

// ============================================================================
// Aggregation + Emission
// ============================================================================

interface AggregationParams {
  readonly ctx: AdapterContext;
  readonly accountId: string;
  readonly parentAccountId: string;
  readonly dateRange: { start: Date; end: Date };
  readonly data: GoogleReviewsResponse;
}

/**
 * Story 23.2 Task 4 (Path A) — pure aggregation helper extracted from
 * `aggregateAndEmit`. Takes raw API reviews + the source `totalReviewCount`
 * + the `accountId` / `parentAccountId` used to build the per-review
 * `location` field, returns ONLY the records array (no metadata stamping,
 * no logging, no AdapterResult wrapping).
 *
 * This is the single source of truth for the records-array shape across
 * BOTH the production path (called from `aggregateAndEmit`) AND the
 * fixture-generator path (called from
 * `packages/integrations/src/fixtures/generators/google-reviews.ts` via
 * the `__testing` export below). Without extraction, the fixture
 * generator's summary math would drift from the adapter's math the first
 * time `aggregateAndEmit` is patched — Round 2 Pre-mortem B mitigation.
 *
 * Returns the records in the canonical order:
 *   1. 1 × `reviewReplySummary` row
 *   2. 3 × `reviewReplyState` rows (APPROVED + PENDING + REJECTED) +
 *      optional 4th `unknown` row when count > 0
 *   3. 3 × `reviewSentiment` rows (positive + neutral + negative)
 *   4. N × `googleReview` rows (per-review records)
 *
 * Pure function — no I/O, no clock reads, no logging. Identical inputs
 * yield byte-identical outputs.
 */
function aggregateReviewsForFixture(
  apiReviews: NonNullable<GoogleReviewsResponse["reviews"]>,
  accountId: string,
  parentAccountId: string,
  // Pass 3 Blind Hunter H1 — Google's `data.totalReviewCount` is the
  // location's full lifetime count (often >> 50); `apiReviews.length`
  // is the page-1 cap (≤ 50). Overview KPI widget needs the true
  // lifetime count. Defaults to `apiReviews.length` for fixture-mode
  // parity (the fixture always emits all reviews — page cap doesn't
  // apply to KV-resident data).
  totalReviewCountFromApi?: number,
): Record<string, unknown>[] {
  // Per-review aggregates
  let ratingOnlyCount = 0;
  let withTextCount = 0;
  let anonymousReviewerCount = 0;
  let approvedReplyCount = 0;
  let pendingReplyCount = 0;
  let rejectedReplyCount = 0;
  let unknownReplyCount = 0;
  let noReplyCount = 0;
  let positiveReviewCount = 0;
  let neutralReviewCount = 0;
  let negativeReviewCount = 0;
  let unknownSentimentCount = 0;
  let replyLatencySumSeconds = 0;
  let replyLatencySamples = 0;
  // Story 35.6 — Batch 1 (Review Media, +4 metrics) + Batch 3 (Review
  // Attributes, +2 metrics). Batch 2 (Reply State Extras, +2 metrics) is
  // derived after the loop from the existing reply-state counters.
  // `reviewerNameSet` is a local Set<string> — its values never reach a
  // record, log, or disk (PII discipline per Story 23.1 AC #18; only the
  // Set.size is surfaced as `reviewerDiversityCount`).
  let reviewsWithPhotosCount = 0;
  let reviewsWithVideosCount = 0;
  let photoMediaItemCount = 0;
  let localizedReviewCount = 0;
  const reviewerNameSet = new Set<string>();
  // Story 35.6 — counts media items with neither `thumbnailUrl` nor
  // `videoUrl`. Surfaces Google schema drift (future additives like
  // `audioUrl`, malformed responses, etc.) before the four batch-1
  // counters silently undercount.
  let unknownMediaItemShapes = 0;
  // Pass 2 Edge Hunter C1 / L2 / L3 — star-bucket counters so the
  // reviewReplySummary row can carry the 5-star distribution AND the
  // GBP-owned metric keys (totalReviewCount / averageRating /
  // reviewsInPeriod / replyRate / fiveStarCount..oneStarCount) that
  // widget presets bind to. Without these, the Overview KPI + Rating
  // Distribution + Reputation Scorecard presets render "no data".
  let starSumForAvg = 0;
  let starSamplesForAvg = 0;
  let fiveStarCount = 0;
  let fourStarCount = 0;
  let threeStarCount = 0;
  let twoStarCount = 0;
  let oneStarCount = 0;

  // Round 1 Code Review — request-scoped log dedup so 50-row pages with
  // all-unknown reply states / star ratings emit 1 log line each, not 50.
  const knownUnknownStates = new Set<string>();
  const knownUnknownStars = new Set<string>();

  const googleReviewRecords: Record<string, unknown>[] = [];

  for (const review of apiReviews) {
    const star = normalizeStarRating(review.starRating, knownUnknownStars);
    const rawComment = review.comment ?? "";
    const hasText = rawComment.trim().length > 0;
    if (hasText) {
      withTextCount++;
    } else {
      ratingOnlyCount++;
    }

    const isAnonymous = review.reviewer?.isAnonymous === true;
    if (isAnonymous) anonymousReviewerCount++;

    let sentimentBucket: "positive" | "neutral" | "negative" | null = null;
    if (star >= 1 && star <= 5) {
      sentimentBucket = sentimentBucketForStar(star);
      if (sentimentBucket === "positive") positiveReviewCount++;
      else if (sentimentBucket === "neutral") neutralReviewCount++;
      else negativeReviewCount++;
      // Pass 2 Edge Hunter C1 — accumulate per-star buckets so the summary
      // record can carry the 5-star distribution.
      starSumForAvg += star;
      starSamplesForAvg++;
      if (star === 5) fiveStarCount++;
      else if (star === 4) fourStarCount++;
      else if (star === 3) threeStarCount++;
      else if (star === 2) twoStarCount++;
      else if (star === 1) oneStarCount++;
    } else {
      unknownSentimentCount++;
    }

    const reviewReply = review.reviewReply;
    const hasReply = reviewReply !== undefined && reviewReply !== null;
    const replyState = hasReply
      ? normalizeReplyState(reviewReply.reviewReplyState, knownUnknownStates)
      : null;

    if (!hasReply) {
      noReplyCount++;
    } else if (replyState === "APPROVED" || replyState === null) {
      // Round 3 Code Review (High) — legacy reply path. Pre-2026-04-01
      // reviews have `reviewReply` present but `reviewReplyState`
      // undefined (Google added the field as additive on 2026-04-01).
      // Per Google's documented additive contract, absence of the field
      // means "pre-existing approved reply" — the reply is publicly
      // visible, otherwise Google would not have surfaced it. Bucket as
      // APPROVED (NOT unknown — previously these were miscounted in
      // pendingOrRejectedReplies, polluting agency dashboards).
      // Latency is a separate metric: include the sample in the mean
      // ONLY when both timestamps are usable. The two metrics now share
      // a `repliedReviewCount` numerator and have independent
      // sample-count denominators (`fetchedReviewCount` vs
      // `replyLatencySamples`), with the divergence documented on the
      // `avgReplyLatencySeconds` metric description.
      approvedReplyCount++;
      const latency = computeReplyLatencySeconds(
        review.createTime,
        reviewReply.updateTime,
      );
      if (latency !== null) {
        replyLatencySumSeconds += latency;
        replyLatencySamples++;
      }
    } else if (replyState === "PENDING") {
      pendingReplyCount++;
    } else if (replyState === "REJECTED") {
      rejectedReplyCount++;
    } else {
      // unknown state (replyState === "unknown" already logged by normalizer)
      unknownReplyCount++;
    }

    // Story 35.6 — Batch 1: photo / video counters. A media item is a
    // photo when it has `thumbnailUrl` AND NO `videoUrl`; a video when
    // it has `videoUrl`. (Google's v4 contract: a single item never
    // carries both; thumbnailUrl on a video item is a poster frame.)
    //
    // Anonymous-reviewer defense-in-depth (symmetric with `hasMedia`
    // emission on the per-review record below + with the fixture
    // generator's anonymous skip): if Google ever sends media on an
    // `isAnonymous: true` review (server-side anonymization regression
    // or future contract change), we DROP it from the summary counters
    // too — otherwise the aggregate KPI would say "1 photo review" while
    // a drill-down filtered by `has_media=true` shows zero rows
    // (summary-vs-detail invariant violation flagged in Story 35.6
    // Round 1 code review).
    //
    // Items with neither `thumbnailUrl` nor `videoUrl` (malformed
    // response OR future additive like `audioUrl`) are emitted as a
    // request-scoped log line so operators can see Google-schema drift
    // BEFORE counts silently degrade — same pattern as
    // `normalizeReplyState`'s unknown-state logging.
    const mediaItems = review.reviewMediaItems;
    if (!isAnonymous && mediaItems !== undefined && mediaItems.length > 0) {
      let photosOnThisReview = 0;
      let hasVideoOnThisReview = false;
      for (const item of mediaItems) {
        if (item.videoUrl !== undefined) {
          hasVideoOnThisReview = true;
        } else if (item.thumbnailUrl !== undefined) {
          photosOnThisReview++;
        } else {
          unknownMediaItemShapes++;
        }
      }
      if (photosOnThisReview > 0) {
        reviewsWithPhotosCount++;
        photoMediaItemCount += photosOnThisReview;
      }
      if (hasVideoOnThisReview) reviewsWithVideosCount++;
    }

    // Story 35.6 — Batch 3: localized review = non-empty comment text AND
    // non-anonymous reviewer (author-identifiable text signal — excludes
    // rating-only + anonymous).
    if (hasText && !isAnonymous) localizedReviewCount++;

    // Story 35.6 — Batch 3: reviewer diversity. Only count non-anonymous
    // reviewers with a non-empty display name. CRITICAL: the sanitization
    // pipeline MUST match the per-review record exactly
    // (`stripCsvInjectionPrefix(sanitizeReviewText(...))`) — otherwise the
    // Set could include names that don't appear on any per-review row,
    // violating the "derivable from per-review reviewerName" invariant
    // that the fixture parity test (Story 35.6 AC #13(d)) asserts. The
    // Set drops on function return — names never persist.
    //
    // Round 2 code review fix: prior implementation used only
    // `sanitizeReviewText`. A displayName like `=cmd("x")` would land in
    // the Set as `=cmd("x")` but on the per-review record as `cmd("x")`
    // (after the formula-char strip) — inflating the diversity count by
    // one phantom entry.
    if (!isAnonymous) {
      const rawDisplayName = review.reviewer?.displayName ?? "";
      const sanitizedName = stripCsvInjectionPrefix(
        sanitizeReviewText(rawDisplayName, REVIEWER_NAME_MAX_LENGTH),
      );
      if (sanitizedName.length > 0) reviewerNameSet.add(sanitizedName);
    }

    // Per-review record — Round 1+2 Code Review PII hardening:
    //  1. CSV-injection prefix strip on ALL free-text fields runs AFTER
    //     sanitizeReviewText (Round 2 fix). Pipeline order:
    //     sanitizeReviewText (HTML + control + bidi strip) →
    //     stripCsvInjectionPrefix (formula char + zero-width strip) →
    //     final value. Earlier order let `"<>=cmd"` slip through because
    //     sanitizeReviewText stripped the HTML, exposing the `=`.
    //  2. `reviewerProfilePhotoUrl` runs through `safeProfilePhotoUrl`
    //     which drops the URL entirely when isAnonymous=true AND when the
    //     host is not Google's documented `lh{N}.googleusercontent.com`.
    //  3. Anonymous reviewers replaced with literal "Anonymous" (NOT
    //     Google's possibly-cached anonymized display name).
    const reviewerNameSafe = isAnonymous
      ? ANONYMOUS_REVIEWER_LABEL
      : stripCsvInjectionPrefix(
          sanitizeReviewText(
            review.reviewer?.displayName ?? "",
            REVIEWER_NAME_MAX_LENGTH,
          ),
        );
    const commentSafe = stripCsvInjectionPrefix(
      sanitizeReviewText(rawComment, REVIEW_COMMENT_MAX_LENGTH),
    );
    const replyCommentSafe = hasReply
      ? stripCsvInjectionPrefix(
          sanitizeReviewText(reviewReply.comment ?? "", REPLY_COMMENT_MAX_LENGTH),
        )
      : undefined;

    googleReviewRecords.push({
      _reportType: "googleReview",
      reviewId: review.reviewId ?? "",
      reviewerName: reviewerNameSafe,
      reviewerIsAnonymous: isAnonymous,
      reviewerProfilePhotoUrl: safeProfilePhotoUrl(
        review.reviewer?.profilePhotoUrl,
        isAnonymous,
      ),
      starRating: star,
      sentimentBucket,
      comment: commentSafe,
      createTime: review.createTime ?? "",
      updateTime: review.updateTime ?? "",
      replyComment: replyCommentSafe,
      // Round 2 Code Review — `undefined` (not empty string) when the
      // reply object exists but `updateTime` is missing. Widget sort
      // logic and Date.parse both behave coherently on `undefined`;
      // `""` parses to `NaN` and sorts before any real timestamp.
      replyUpdateTime: hasReply ? reviewReply.updateTime : undefined,
      replyState,
      hasReply,
      // Story 35.6 — backs the `has_media` dimension. Boolean derivation
      // from `reviewMediaItems` non-empty. Anonymous reviewers always emit
      // `false` (Google's server-side anonymization strips media for
      // anonymous reviewers per 2026-05-19 docs; defense-in-depth here).
      hasMedia: !isAnonymous && mediaItems !== undefined && mediaItems.length > 0,
      location: `accounts/${parentAccountId}/locations/${accountId}`,
    });
  }

  const fetchedReviewCount = apiReviews.length;
  const repliedReviewCount = approvedReplyCount;
  const pendingOrRejectedReplies =
    pendingReplyCount + rejectedReplyCount + unknownReplyCount;
  const unrepliedReviewCount = noReplyCount;

  // Round 2 Code Review — AC #8 requires "zero counts in each row" for
  // the empty-state path. Round 1's `null` for replyRate /
  // ratingOnlyPercentage broke the AC #5 numeric schema and forced widget
  // renderers to handle `number | null`. Reverted to numeric zero;
  // distinguish "no data" via `metadata.emptyStateReason` instead.
  const replyRate =
    fetchedReviewCount > 0
      ? Math.round((repliedReviewCount / fetchedReviewCount) * 1000) / 10
      : 0;

  // `avgReplyLatencySeconds` stays nullable — AC #10 explicitly says
  // "NOT 0 — `0 seconds` would render as a misleading 'instant reply'
  // indicator". Null is the spec-correct value here.
  const avgReplyLatencySeconds =
    replyLatencySamples > 0
      ? Math.round(replyLatencySumSeconds / replyLatencySamples)
      : null;

  const ratingOnlyPercentage =
    fetchedReviewCount > 0
      ? Math.round((ratingOnlyCount / fetchedReviewCount) * 1000) / 10
      : 0;

  // Story 35.6 — Batch 1 derived: media-attachment rate is (photos +
  // videos) / total reviews × 100, rounded to 1 decimal. Zero-state path:
  // emit 0 (matches the existing `replyRate` / `ratingOnlyPercentage`
  // numeric-zero convention). Per AC #4 this is NOT null in the zero
  // state (only `approvalRate` + `rejectedReplyRate` use null because
  // their denominator is "reply attempts", a separate signal from "no
  // data").
  const mediaAttachmentRate =
    fetchedReviewCount > 0
      ? Math.round(
          ((reviewsWithPhotosCount + reviewsWithVideosCount) / fetchedReviewCount) *
            1000,
        ) / 10
      : 0;

  // Story 35.6 — Batch 2: reply-state ratios. Denominator is APPROVED +
  // PENDING + REJECTED (canonical states only — unknown reply states do
  // NOT count toward the denominator per Story 23.1 AC #9 graceful-
  // degradation). Per AC #4 zero-state: when total reply attempts === 0
  // both ratios emit as `null` (a misleading "0% approval / 0% rejection"
  // would render as "the agency rejected everything" instead of "no reply
  // attempts yet"). Mirrors the `avgReplyLatencySeconds: null` precedent.
  const totalReplyAttempts =
    approvedReplyCount + pendingReplyCount + rejectedReplyCount;
  const approvalRate =
    totalReplyAttempts > 0
      ? Math.round((approvedReplyCount / totalReplyAttempts) * 1000) / 10
      : null;
  const rejectedReplyRate =
    totalReplyAttempts > 0
      ? Math.round((rejectedReplyCount / totalReplyAttempts) * 1000) / 10
      : null;

  // Story 35.6 — Batch 3 derived: reviewerDiversityCount is the count of
  // distinct non-anonymous, non-empty display names. The Set's contents
  // are local to this function — never persisted.
  const reviewerDiversityCount = reviewerNameSet.size;

  // Story 35.6 (Round 1 code review fix) — surface Google schema drift
  // when reviewMediaItems contains shapes with neither thumbnailUrl nor
  // videoUrl. One log line per fetch (sum across all reviews in the
  // page), matching the request-scoped dedup pattern used by
  // normalizeReplyState's `knownUnknownStates`. Zero is the silent-OK
  // case (no emission). The log carries the count so operators can size
  // the drift before deciding whether to extend the per-shape branch.
  if (unknownMediaItemShapes > 0) {
    console.log(
      JSON.stringify({
        event: "google_reviews_unknown_media_item_shape",
        count: unknownMediaItemShapes,
        timestamp: new Date().toISOString(),
      }),
    );
  }

  // ── Build records array ──
  // Always emit all 3 canonical reply-state rows (APPROVED + PENDING +
  // REJECTED) plus `unknown` only when count > 0.
  const replyStateRecords: Record<string, unknown>[] = [
    { _reportType: "reviewReplyState", replyState: "APPROVED", count: approvedReplyCount },
    { _reportType: "reviewReplyState", replyState: "PENDING", count: pendingReplyCount },
    { _reportType: "reviewReplyState", replyState: "REJECTED", count: rejectedReplyCount },
  ];
  if (unknownReplyCount > 0) {
    replyStateRecords.push({
      _reportType: "reviewReplyState",
      replyState: "unknown",
      count: unknownReplyCount,
    });
  }

  // Round 3 Code Review (AC #5) — exactly 3 sentiment rows
  // (positive/neutral/negative) per spec. `unknownSentimentCount`
  // (STAR_RATING_UNSPECIFIED and additive Google enum) is surfaced via
  // metadata only, not as a 4th record. Widget chart stability is
  // preserved (always-3-bars-regardless-of-count contract).
  const sentimentRecords: Record<string, unknown>[] = [
    { _reportType: "reviewSentiment", sentimentBucket: "positive", count: positiveReviewCount },
    { _reportType: "reviewSentiment", sentimentBucket: "neutral", count: neutralReviewCount },
    { _reportType: "reviewSentiment", sentimentBucket: "negative", count: negativeReviewCount },
  ];

  // Round 3 Code Review (AC #5) — `reviewReplySummary` record contains
  // the 9 fields AC #5 enumerates.
  //
  // Pass 2 Edge Hunter C1 / L2 / L3 — EXTENDED with the metric keys widget
  // presets bind to: 5-star distribution + GBP-owned base metrics
  // (totalReviewCount/averageRating/replyRate/reviewsInPeriod) + 2
  // Story 23.2 synthetic-aggregate metrics. Without these, 5 of 8 presets
  // would render "no data" because their `metricKey` config doesn't
  // resolve to any field in any emitted record. Per AC #11
  // synthetic-aggregate definition:
  //   - reviewVelocityPerMonth: rolling 30-day review count. Since the
  //     adapter doesn't track a "30-day window" semantically, we
  //     conservatively report it as the fetched count (page-1-of-50 cap
  //     is roughly "the most recent" reviews — close enough for the
  //     scorecard widget).
  //   - reputationTrendDirection: 1 = improving, 0 = flat, -1 = declining.
  //     Computed from the average rating of the most-recent 1/3 of
  //     fetched reviews vs the overall average. Conservative default 0
  //     when fewer than 3 reviews are present.
  const overallAvgRating =
    starSamplesForAvg > 0 ? starSumForAvg / starSamplesForAvg : 0;

  // Pass 3 Edge Hunter H1 — sort google review records by createTime DESC
  // BEFORE extracting the recent-window slice for trend computation.
  // Google's v4 reviews list endpoint is documented to return reviews in
  // updateTime-DESC order, but downstream consumers must not depend on
  // upstream ordering. Sorting here makes `reputationTrendDirection`
  // order-independent.
  const sortedByCreateTimeDesc = [...googleReviewRecords].sort((a, b) => {
    const aMs = Date.parse((a.createTime as string) ?? "") || 0;
    const bMs = Date.parse((b.createTime as string) ?? "") || 0;
    return bMs - aMs;
  });
  const recentWindowSize = Math.max(1, Math.floor(starSamplesForAvg / 3));
  let recentStarSum = 0;
  let recentStarSamples = 0;
  for (const rec of sortedByCreateTimeDesc) {
    if (recentStarSamples >= recentWindowSize) break;
    const recStar = rec.starRating;
    // Pass 3 Edge Hunter M2 — Number.isFinite guard (defense-in-depth;
    // matches the Pass 2 Number.isFinite guards in aggregateAndEmit
    // rewalk).
    if (typeof recStar === "number" && Number.isFinite(recStar) && recStar >= 1 && recStar <= 5) {
      recentStarSum += recStar;
      recentStarSamples++;
    }
  }
  const recentAvgRating =
    recentStarSamples > 0 ? recentStarSum / recentStarSamples : overallAvgRating;
  const reputationTrendDirection =
    starSamplesForAvg < 3
      ? 0
      : recentAvgRating > overallAvgRating + 0.05
        ? 1
        : recentAvgRating < overallAvgRating - 0.05
          ? -1
          : 0;

  // Pass 3 Blind Hunter H2 / Edge Hunter H2/H3 — `reviewsInPeriod` is the
  // rolling 30-day review count (matches AC #1 / catalogue semantic).
  // `reviewVelocityPerMonth` is the SAME 30-day count (alias — they both
  // mean "reviews per month" in the agency-reporting context). Compute
  // from googleReviewRecords with a 30-day cutoff anchored to the LATEST
  // review's createTime — this is stable across time (production data
  // updates as new reviews land; fixture-mode data is frozen and the
  // anchor stays at the fixture's most-recent review). Using Date.now()
  // would cause fixtures to decay (15 reviews in period today, 0
  // tomorrow because all createTime values are 1+ day old).
  const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
  let latestCreateMs = 0;
  for (const rec of googleReviewRecords) {
    const ms = Date.parse((rec.createTime as string) ?? "") || 0;
    if (ms > latestCreateMs) latestCreateMs = ms;
  }
  const cutoffMs = latestCreateMs > 0 ? latestCreateMs - THIRTY_DAYS_MS : 0;
  let reviewsInPeriod = 0;
  for (const rec of googleReviewRecords) {
    const ct = rec.createTime as string | undefined;
    // Strict `>` so a review exactly 30 days before the latest one is
    // EXCLUDED (matches "rolling 30-day window" semantic: includes today
    // back to but not including 30-days-ago). Without strict-gt, fixture
    // edge case at the boundary day counts as "in period" + "out of
    // period" simultaneously depending on render time.
    if (ct && Date.parse(ct) > cutoffMs) reviewsInPeriod++;
  }
  // Pass 3 Blind Hunter H1 — surface Google's TRUE lifetime totalReviewCount
  // (not the page-1 fetched count). Fixture-mode default keeps it identical
  // to fetched count.
  const summaryTotalReviewCount =
    totalReviewCountFromApi !== undefined &&
    Number.isFinite(totalReviewCountFromApi)
      ? totalReviewCountFromApi
      : fetchedReviewCount;
  const summaryRecord: Record<string, unknown> = {
    _reportType: "reviewReplySummary",
    repliedReviewCount,
    unrepliedReviewCount,
    replyRate,
    avgReplyLatencySeconds,
    pendingOrRejectedReplies,
    ratingOnlyCount,
    ratingOnlyPercentage,
    withTextCount,
    anonymousReviewerCount,
    // Story 35.6 — Batch 1: Review Media (4 metrics) — Google 2026-04-20
    // reviewMediaItems API additive. Counters increment in the per-review
    // loop above; mediaAttachmentRate is derived post-loop.
    reviewsWithPhotosCount,
    reviewsWithVideosCount,
    photoMediaItemCount,
    mediaAttachmentRate,
    // Story 35.6 — Batch 2: Reply State Extras (2 metrics). null in the
    // zero-state path (no reply attempts) — see derivation comment above.
    approvalRate,
    rejectedReplyRate,
    // Story 35.6 — Batch 3: Review Attributes (2 metrics). Reviewer
    // cardinality + author-identifiable text-review count. The reviewer
    // names themselves never reach a record (PII discipline).
    localizedReviewCount,
    reviewerDiversityCount,
    // Pass 2 Edge Hunter C1 / L2 / L3 — additional fields preset widgets
    // bind to. AC #11 synthetic-aggregates + 5-star distribution + GBP-
    // owned base metrics (replicated here so the renderer reads them from
    // a single record without cross-platform joins).
    //
    // Pass 3 corrections:
    //   - totalReviewCount: now Google's TRUE lifetime count (or fetched
    //     count for fixture mode where the page cap doesn't apply).
    //   - reviewsInPeriod: derived from the 30-day window (not the page
    //     count).
    //   - reviewVelocityPerMonth: same 30-day count — semantically the
    //     "reviews per month" pace metric.
    totalReviewCount: summaryTotalReviewCount,
    averageRating: Math.round(overallAvgRating * 10) / 10,
    reviewsInPeriod,
    fiveStarCount,
    fourStarCount,
    threeStarCount,
    twoStarCount,
    oneStarCount,
    reviewVelocityPerMonth: reviewsInPeriod,
    reputationTrendDirection,
  };

  const allRecords: Record<string, unknown>[] = [
    summaryRecord,
    ...replyStateRecords,
    ...sentimentRecords,
    ...googleReviewRecords,
  ];

  return allRecords;
}

/**
 * Story 23.2 Task 4 — `aggregateAndEmit` now delegates the records-array
 * construction to `aggregateReviewsForFixture` (the pure extracted helper
 * above) and keeps responsibility for metadata stamping, structured log
 * emission, and `AdapterResult` wrapping.
 */
function aggregateAndEmit(
  params: AggregationParams,
): AdapterResult<AdapterMetrics> {
  const { ctx, accountId, parentAccountId, dateRange, data } = params;
  const apiReviews = data.reviews ?? [];
  const totalReviewCount = data.totalReviewCount ?? 0;
  const fetchedReviewCount = apiReviews.length;

  // Round 2 Code Review — REMOVED the Round 1 date-range post-filter.
  // Per Dev Notes "Date-range filtering caveat": "The v4 endpoint doesn't
  // support a date filter… This is the same constraint GBP operates under
  // and is acceptable — document in adapter source. The widget renderer
  // (Story 23.2 widget presets) MUST surface a 'data based on most recent
  // 50 reviews' footnote where these metrics appear." The Round 1
  // post-filter was over-enforcement that diverged from GBP parity. The
  // `dataIsPartial` metadata flag (below) is the spec-compliant signal
  // for the widget renderer to surface the footnote.

  const allRecords = aggregateReviewsForFixture(
    apiReviews,
    accountId,
    parentAccountId,
    // Pass 3 Blind Hunter H1 — pass Google's TRUE lifetime count so the
    // summary record's totalReviewCount reflects reality (not page-1 cap).
    totalReviewCount,
  );

  // Derive forensic counts for the completion log + metadata side-channel.
  // We re-walk the records array to extract the values the log needs;
  // cheaper than passing them out-of-band from the pure helper.
  //
  // Pass 1 Blind Hunter H3 — use `+=` for approvedReplyCount defense-in-
  // depth (if the helper ever emits multiple APPROVED reply-state rows for
  // some pathological reason, `=` would silently drop counts).
  //
  // Note: `pendingOrRejectedReplies` here is the COMPLETION-LOG payload
  // value only, and intentionally aggregates PENDING + REJECTED + unknown
  // (matches the catalogue metric definition at GOOGLE_REVIEWS_METRICS:
  // "Replies that have not yet been APPROVED (PENDING/REJECTED/unknown)").
  // The forensic-log `pendingOrRejectedReplies` field is the SAME concept.
  // Future agents: do NOT split this into pendingOnly/rejectedOnly without
  // also updating GOOGLE_REVIEWS_METRICS description + tests.
  //
  // Pass 1 Blind Hunter H3 — `unknownSentimentCount` only fires on
  // googleReview rows with sentimentBucket === null, which happens when
  // normalizeStarRating returns 0 (e.g., Google's STAR_RATING_UNSPECIFIED
  // or a future additive enum). Production payloads with unknown stars
  // surface this count in metadata.unknownSentimentCount (see below).
  let approvedReplyCount = 0;
  let pendingOrRejectedReplies = 0;
  let ratingOnlyCount = 0;
  let unknownSentimentCount = 0;
  for (const rec of allRecords) {
    if (rec._reportType === "reviewReplyState") {
      const state = rec.replyState as string;
      // Pass 2 Blind Hunter H3 — guard against NaN / non-number `count`
      // (nullish-coalescing `?? 0` only skips null/undefined, not NaN).
      const rawCount = rec.count;
      const count =
        typeof rawCount === "number" && Number.isFinite(rawCount) ? rawCount : 0;
      if (state === "APPROVED") approvedReplyCount += count;
      else if (state === "PENDING" || state === "REJECTED" || state === "unknown") {
        pendingOrRejectedReplies += count;
      }
    } else if (rec._reportType === "reviewReplySummary") {
      const rawROC = rec.ratingOnlyCount;
      ratingOnlyCount =
        typeof rawROC === "number" && Number.isFinite(rawROC) ? rawROC : 0;
    } else if (rec._reportType === "googleReview" && rec.sentimentBucket === null) {
      unknownSentimentCount++;
    }
  }

  // Round 2 Code Review — `dataIsPartial` derived from the SOURCE
  // page-1-of-50 cap. Widget renderers surface "based on most recent 50
  // reviews" footnote when true.
  const dataIsPartial = totalReviewCount > fetchedReviewCount;
  const metadata: Record<string, unknown> = {
    dataFreshnessLagHours: 0,
    dataIsPartial,
    totalReviewCount,
    fetchedReviewCount,
  };
  if (unknownSentimentCount > 0) {
    metadata.unknownSentimentCount = unknownSentimentCount;
  }
  // Round 1 Code Review #16 — google_returned_zero is a distinguishable
  // empty state surfaced via metadata only (the completion log keeps the
  // exact AC #17 field set unchanged).
  if (apiReviews.length === 0) {
    metadata.emptyStateReason = "google_returned_zero";
  }

  // Round 3 Code Review (AC #17 strict) — completion log emits EXACTLY
  // the 10 fields enumerated in AC #17. `dataIsPartial` /
  // `emptyStateReason` live in metadata only — adding them to the log
  // would violate the AC's "with these fields" wording.
  console.log(
    JSON.stringify({
      event: "google_reviews_fetch_complete",
      integrationId: ctx.integrationId,
      tenantId: ctx.tenantId,
      reviewsFetched: fetchedReviewCount,
      totalReviewCount,
      approvedReplies: approvedReplyCount,
      pendingOrRejectedReplies,
      ratingOnlyCount,
      recordCount: allRecords.length,
      timestamp: new Date().toISOString(),
    }),
  );

  return adapterOk({
    platform: PLATFORM,
    accountId,
    dateRange: { start: dateRange.start, end: dateRange.end },
    records: allRecords,
    fetchedAt: new Date(),
    recordCount: allRecords.length,
    metadata,
  });
}

interface EmptyStateParams {
  readonly ctx: AdapterContext;
  readonly accountId: string;
  readonly dateRange: { start: Date; end: Date };
  readonly reason: EmptyStateReason;
}

/**
 * Emit a coherent zero-state when no review data is available. Returns the
 * same `_reportType` shapes so widget renderers see a stable schema.
 *
 * Round 2 Code Review:
 *   - `recordCount === records.length` invariant preserved (was Round 1
 *     `recordCount: 0` which broke downstream callers depending on the
 *     `recordCount === records.length` invariant).
 *   - Numeric zero rates (replyRate: 0, ratingOnlyPercentage: 0) — AC #8
 *     mandates "zero counts in each row"; widgets distinguish "no data"
 *     via `metadata.emptyStateReason` instead of nullable numerics.
 *   - `avgReplyLatencySeconds: null` is preserved per AC #10 ("NOT 0 —
 *     0 seconds would render as a misleading 'instant reply' indicator").
 */
function emitEmptyState(params: EmptyStateParams): AdapterResult<AdapterMetrics> {
  const { ctx, accountId, dateRange, reason } = params;

  const summaryRecord: Record<string, unknown> = {
    _reportType: "reviewReplySummary",
    repliedReviewCount: 0,
    unrepliedReviewCount: 0,
    replyRate: 0,
    avgReplyLatencySeconds: null,
    pendingOrRejectedReplies: 0,
    ratingOnlyCount: 0,
    ratingOnlyPercentage: 0,
    withTextCount: 0,
    anonymousReviewerCount: 0,
    // Story 35.6 — Batch 1: 4 photo/video counters all 0 in zero state.
    reviewsWithPhotosCount: 0,
    reviewsWithVideosCount: 0,
    photoMediaItemCount: 0,
    mediaAttachmentRate: 0,
    // Story 35.6 — Batch 2: ratios are `null` in zero state (no reply
    // attempts = no coherent percentage). Mirrors avgReplyLatencySeconds.
    approvalRate: null,
    rejectedReplyRate: null,
    // Story 35.6 — Batch 3: reviewer-attribute counters all 0 in zero state.
    localizedReviewCount: 0,
    reviewerDiversityCount: 0,
    positiveReviewCount: 0,
    neutralReviewCount: 0,
    negativeReviewCount: 0,
    fetchedReviewCount: 0,
    totalReviewCount: 0,
  };

  const replyStateRecords: Record<string, unknown>[] = [
    { _reportType: "reviewReplyState", replyState: "APPROVED", count: 0 },
    { _reportType: "reviewReplyState", replyState: "PENDING", count: 0 },
    { _reportType: "reviewReplyState", replyState: "REJECTED", count: 0 },
  ];
  const sentimentRecords: Record<string, unknown>[] = [
    { _reportType: "reviewSentiment", sentimentBucket: "positive", count: 0 },
    { _reportType: "reviewSentiment", sentimentBucket: "neutral", count: 0 },
    { _reportType: "reviewSentiment", sentimentBucket: "negative", count: 0 },
  ];

  const allRecords: Record<string, unknown>[] = [
    summaryRecord,
    ...replyStateRecords,
    ...sentimentRecords,
  ];

  // Round 3 Code Review (AC #17 strict) — completion log emits the exact
  // 10-field schema. emptyStateReason lives in metadata only.
  console.log(
    JSON.stringify({
      event: "google_reviews_fetch_complete",
      integrationId: ctx.integrationId,
      tenantId: ctx.tenantId,
      reviewsFetched: 0,
      totalReviewCount: 0,
      approvedReplies: 0,
      pendingOrRejectedReplies: 0,
      ratingOnlyCount: 0,
      recordCount: allRecords.length,
      timestamp: new Date().toISOString(),
    }),
  );

  return adapterOk({
    platform: PLATFORM,
    accountId,
    dateRange: { start: dateRange.start, end: dateRange.end },
    records: allRecords,
    fetchedAt: new Date(),
    // Round 2 Code Review — recordCount matches records.length so the
    // invariant downstream consumers depend on holds. Widget renderers
    // distinguish "no data" via metadata.emptyStateReason.
    recordCount: allRecords.length,
    metadata: {
      dataFreshnessLagHours: 0,
      dataIsPartial: false,
      emptyStateReason: reason,
      totalReviewCount: 0,
      fetchedReviewCount: 0,
    },
  });
}

// ============================================================================
// Test-only exports
// ============================================================================

/**
 * Test-only exports so unit tests can verify the rule-based sentiment
 * bucketing and reply-state normalization without re-implementing the logic.
 */
export const __testing = {
  sentimentBucketForStar,
  normalizeReplyState,
  normalizeStarRating,
  computeReplyLatencySeconds,
  stripCsvInjectionPrefix,
  safeProfilePhotoUrl,
  CSV_INJECTION_LEADING_CHARS,
  STRICT_ISO_8601_TZ,
  MAX_SANE_REPLY_LATENCY_SECONDS,
  GOOGLE_REVIEWS_METRICS,
  GOOGLE_REVIEWS_DIMENSIONS,
  // Story 23.2 Task 4 (Path A) — pure aggregation helper exposed so the
  // FDL generator at packages/integrations/src/fixtures/generators/
  // google-reviews.ts builds its records array via the SAME logic the
  // production adapter uses (no drift over time).
  aggregateReviewsForFixture,
};

export type GoogleReviewsReplyState = ReviewReplyState;
